import React, { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';

const OrganizationVerificationQueue = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [minConfidence, setMinConfidence] = useState(60);
  const [verifying, setVerifying] = useState(null);

  const LIMIT = 20;

  const loadSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await crmService.getUnverifiedOrgLinks({
        limit: LIMIT,
        offset,
        min_confidence: minConfidence
      });
      setSuggestions(response.suggestions || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [offset, minConfidence]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const handleVerify = async (linkId, contactId, orgId) => {
    try {
      setVerifying(linkId);
      await crmService.verifyOrgLink({
        contact_id: contactId,
        org_id: orgId,
        verified_by: 'admin'
      });
      // Remove from list
      setSuggestions(suggestions.filter(s => s.link_id !== linkId));
      setTotal(Math.max(0, total - 1));
    } catch (err) {
      console.error('Verification failed:', err);
    } finally {
      setVerifying(null);
    }
  };

  const handleReject = async (linkId, contactId, orgId) => {
    try {
      setVerifying(linkId);
      await crmService.rejectOrgLink({
        contact_id: contactId,
        org_id: orgId
      });
      // Remove from list
      setSuggestions(suggestions.filter(s => s.link_id !== linkId));
      setTotal(Math.max(0, total - 1));
    } catch (err) {
      console.error('Rejection failed:', err);
    } finally {
      setVerifying(null);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return '#22D3EE'; // Cyan - high confidence (proven pathway)
    if (confidence >= 60) return '#c4a563'; // Gold - medium confidence (explored territory)
    return '#6B7280'; // Gray - low confidence (uncharted territory)
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 95) return 'Exact Match — Proven Pathway';
    if (confidence >= 75) return 'Strong Match — Explored Territory';
    if (confidence >= 60) return 'Likely Match — Scout Report';
    return 'Possible Match — Uncharted';
  };

  const getMethodLabel = (method) => {
    const labels = {
      'auto_title': 'From Title',
      'auto_notes': 'From Notes',
      'manual': 'Manual Link',
      'verified': 'Verified'
    };
    return labels[method] || method;
  };

  if (loading && suggestions.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: 'var(--text-muted)'
      }}>
        Loading Scout Reports...
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: 'var(--text-muted)',
        borderTop: '1px solid var(--border)'
      }}>
        <p style={{ margin: 0, fontSize: '0.9rem' }}>✓ All organizational connections verified!</p>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', fontStyle: 'italic' }}>
          The herd knows its territory.
        </p>
      </div>
    );
  }

  const verified = total - suggestions.length;
  const progress = total > 0 ? Math.round((verified / total) * 100) : 0;

  return (
    <div style={{
      borderTop: '1px solid var(--border)',
      padding: '1.5rem'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: '1.1rem',
            fontFamily: "'League Spartan', sans-serif",
            fontWeight: 600,
            color: 'var(--text)'
          }}>
            Uncharted Territory
          </h3>
          <p style={{
            margin: '0.25rem 0 0 0',
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
            fontStyle: 'italic'
          }}>
            Scout reports awaiting verification · {verified} of {total} blazed trails
          </p>
        </div>
        <div style={{
          fontSize: '0.9rem',
          color: 'var(--accent)',
          fontWeight: 600
        }}>
          {progress}% Verified
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        width: '100%',
        height: '6px',
        backgroundColor: 'var(--border)',
        borderRadius: '3px',
        overflow: 'hidden',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          backgroundColor: 'var(--accent)',
          transition: 'width 0.3s ease'
        }} />
      </div>

      {/* Confidence filter */}
      <div style={{
        display: 'flex',
        gap: 'clamp(0.25rem, 1vw, 0.5rem)',
        marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
        flexWrap: 'wrap'
      }}>
        {[60, 70, 80, 90].map(conf => (
          <button
            key={conf}
            onClick={() => {
              setMinConfidence(conf);
              setOffset(0);
            }}
            style={{
              padding: 'clamp(0.25rem, 1vw, 0.375rem) clamp(0.5rem, 2vw, 0.75rem)',
              border: minConfidence === conf ? '1px solid var(--accent)' : '1px solid var(--border)',
              backgroundColor: minConfidence === conf ? 'rgba(34, 211, 238, 0.1)' : 'transparent',
              color: minConfidence === conf ? 'var(--accent)' : 'var(--text-muted)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontSize: 'clamp(0.75rem, 2.5vw, 0.8rem)',
              transition: 'all 0.2s',
              fontWeight: minConfidence === conf ? 600 : 400,
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {conf}%+
          </button>
        ))}
      </div>

      {/* Suggestions list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(0.75rem, 2vw, 1rem)' }}>
        {suggestions.map(sugg => (
          <div
            key={sugg.link_id}
            style={{
              border: `1px solid var(--border)`,
              borderRadius: 'var(--radius-md)',
              padding: 'clamp(0.75rem, 2vw, 1rem)',
              backgroundColor: 'rgba(34, 211, 238, 0.02)',
              transition: 'all 0.2s',
              opacity: verifying === sugg.link_id ? 0.6 : 1
            }}
          >
            {/* Contact + Org */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '0.75rem'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: 'var(--text)',
                  marginBottom: '0.25rem'
                }}>
                  {sugg.first_name} {sugg.last_name}
                  {sugg.rank_code && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> ({sugg.rank_code})</span>}
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-muted)',
                  marginBottom: '0.5rem'
                }}>
                  {sugg.title}
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: 'var(--accent)',
                  fontWeight: 500
                }}>
                  → {sugg.org_name} {sugg.org_acronym ? `(${sugg.org_acronym})` : ''}
                </div>
              </div>

              {/* Confidence badge */}
              <div style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: getConfidenceColor(sugg.match_confidence),
                color: sugg.match_confidence >= 80 ? '#0D1F3C' : sugg.match_confidence >= 60 ? '#0D1F3C' : '#F1F5F9',
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                marginLeft: '1rem',
                minWidth: '70px',
                border: `1px solid ${getConfidenceColor(sugg.match_confidence)}`
              }}>
                {sugg.match_confidence}%
              </div>
            </div>

            {/* Metadata */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              marginBottom: '0.75rem',
              borderTop: '1px solid var(--border)',
              paddingTop: '0.75rem'
            }}>
              <span>{getConfidenceLabel(sugg.match_confidence)}</span>
              <span>•</span>
              <span>{getMethodLabel(sugg.match_method)}</span>
              {sugg.service_branch && (
                <>
                  <span>•</span>
                  <span>{sugg.service_branch}</span>
                </>
              )}
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: 'clamp(0.5rem, 1vw, 0.75rem)',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => handleVerify(sugg.link_id, sugg.contact_id, sugg.org_id)}
                disabled={verifying === sugg.link_id}
                style={{
                  flex: '1 1 140px',
                  minHeight: '40px',
                  padding: 'clamp(0.4rem, 1vw, 0.6rem) clamp(0.75rem, 2vw, 1rem)',
                  backgroundColor: '#22D3EE',
                  color: '#0D1F3C',
                  border: '1px solid #22D3EE',
                  borderRadius: 'var(--radius-sm)',
                  cursor: verifying === sugg.link_id ? 'wait' : 'pointer',
                  fontWeight: 600,
                  fontSize: 'clamp(0.8rem, 2.5vw, 0.85rem)',
                  transition: 'all 0.2s',
                  opacity: verifying === sugg.link_id ? 0.7 : 1,
                  boxShadow: verifying === sugg.link_id ? 'none' : '0 2px 8px rgba(34, 211, 238, 0.1)'
                }}
              >
                ✓ Blaze Trail
              </button>
              <button
                onClick={() => handleReject(sugg.link_id, sugg.contact_id, sugg.org_id)}
                disabled={verifying === sugg.link_id}
                style={{
                  flex: '1 1 120px',
                  minHeight: '40px',
                  padding: 'clamp(0.4rem, 1vw, 0.6rem) clamp(0.75rem, 2vw, 1rem)',
                  backgroundColor: 'transparent',
                  color: '#0D1F3C',
                  border: '1px solid #0D1F3C',
                  borderRadius: 'var(--radius-sm)',
                  cursor: verifying === sugg.link_id ? 'wait' : 'pointer',
                  fontWeight: 500,
                  fontSize: 'clamp(0.8rem, 2.5vw, 0.85rem)',
                  transition: 'all 0.2s',
                  opacity: verifying === sugg.link_id ? 0.7 : 1
                }}
              >
                ✕ Reject
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 'clamp(0.5rem, 1vw, 0.75rem)',
          marginTop: 'clamp(1rem, 2vw, 1.5rem)',
          borderTop: '1px solid var(--border)',
          paddingTop: 'clamp(0.75rem, 1.5vw, 1rem)',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={offset === 0}
            style={{
              padding: 'clamp(0.3rem, 1vw, 0.375rem) clamp(0.6rem, 1.5vw, 0.75rem)',
              minHeight: '40px',
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: offset === 0 ? 'var(--text-muted)' : 'var(--accent)',
              borderRadius: 'var(--radius-sm)',
              cursor: offset === 0 ? 'default' : 'pointer',
              fontSize: 'clamp(0.8rem, 2.5vw, 0.85rem)'
            }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.85rem)', color: 'var(--text-muted)', alignSelf: 'center' }}>
            {offset / LIMIT + 1} of {Math.ceil(total / LIMIT)}
          </span>
          <button
            onClick={() => setOffset(offset + LIMIT)}
            disabled={offset + LIMIT >= total}
            style={{
              padding: 'clamp(0.3rem, 1vw, 0.375rem) clamp(0.6rem, 1.5vw, 0.75rem)',
              minHeight: '40px',
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: offset + LIMIT >= total ? 'var(--text-muted)' : 'var(--accent)',
              borderRadius: 'var(--radius-sm)',
              cursor: offset + LIMIT >= total ? 'default' : 'pointer',
              fontSize: 'clamp(0.8rem, 2.5vw, 0.85rem)'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default OrganizationVerificationQueue;
